import { Locales, Column, QuestionType } from "@simplyk/common";
import { UseFormSetValue, useFormContext } from "react-hook-form";

import { useTranslateRows } from "../helpers/useTranslateRows";
import { DonationTranslationsData, TicketingTranslationsData } from "../translation.types";

interface SavedQuestionTranslation {
  questionId?: string;
  body?: string | null;
  choices?: Array<{ id: string; value?: string | null }>;
}

interface TranslationQuestion {
  id: string;
  type: QuestionType;
  questionId?: string;
  destinationIndex?: number;
  questionFields?: Array<{ locale: string; body?: string | null }> | null;
  choices?: Array<{
    id: string;
    sortIndex?: number;
    choiceFields?: Array<{ locale: string; body?: string | null }> | null;
  }> | null;
}

export function useTranslateQuestions({
  questions,
  formLocale,
  translationLocale,
  setValue,
  existingTranslations,
}: {
  questions: TranslationQuestion[];
  formLocale: Locales;
  translationLocale: Locales;
  setValue: UseFormSetValue<TicketingTranslationsData> | UseFormSetValue<DonationTranslationsData>;
  existingTranslations?: ReadonlyArray<SavedQuestionTranslation | undefined>;
}) {
  const form = useFormContext<TicketingTranslationsData | DonationTranslationsData>();
  const destinationQuestions = form?.getValues(`${Column.Translations}.questionsFields`);
  const { isTranslating, translatedIds } = useTranslateRows<TicketingTranslationsData | DonationTranslationsData>({
    translationLocale,
    setValue: (path, value) =>
      (setValue as UseFormSetValue<TicketingTranslationsData>)(
        path as `translations.questionsFields.${number}.body`,
        value
      ),
    rows: questions.map((question, index) => {
      const questionId = question.questionId ?? question.id;
      const matchedIndex = destinationQuestions?.findIndex((destination) => destination.questionId === questionId);
      const destinationIndex =
        question.destinationIndex ?? (matchedIndex !== undefined && matchedIndex >= 0 ? matchedIndex : index);
      const saved =
        existingTranslations?.find((destination) => destination?.questionId === questionId) ??
        existingTranslations?.[destinationIndex];
      const destination = destinationQuestions?.[destinationIndex];
      return {
        id: question.id,
        fields:
          question.type === QuestionType.Name
            ? []
            : [
                {
                  key: "title",
                  path: `${Column.Translations}.questionsFields.${destinationIndex}.body`,
                  source: question.questionFields?.find((field) => field.locale === formLocale)?.body ?? "",
                  saved:
                    saved?.body ?? question.questionFields?.find((field) => field.locale === translationLocale)?.body,
                  isPlainText: true,
                },
                ...(question.choices ?? [])
                  .slice()
                  .sort((first, second) => (first.sortIndex ?? 0) - (second.sortIndex ?? 0))
                  .map((choice, choiceIndex) => {
                    const matchedChoiceIndex = destination?.choices?.findIndex((field) => field.id === choice.id);
                    const destinationChoiceIndex =
                      matchedChoiceIndex !== undefined && matchedChoiceIndex >= 0 ? matchedChoiceIndex : choiceIndex;
                    return {
                      key: `choice_${choice.id}`,
                      path: `${Column.Translations}.questionsFields.${destinationIndex}.choices.${destinationChoiceIndex}.value`,
                      source: choice.choiceFields?.find((field) => field.locale === formLocale)?.body ?? "",
                      saved:
                        saved?.choices?.find((field) => field.id === choice.id)?.value ??
                        choice.choiceFields?.find((field) => field.locale === translationLocale)?.body,
                      isPlainText: true,
                    };
                  }),
              ],
      };
    }),
  });

  return { isTranslating, translatedQuestionFieldIds: translatedIds };
}
